import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Store, Star, Trash2, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import { AccessGatedAction } from "@/components/AccessGatedAction";
import {
  listOwnedLocations,
  renameLocation,
  setDefaultLocation,
  archiveLocation,
  getLocationCap,
} from "@/lib/locations.functions";
import { withAuth } from "@/lib/server-fn-auth";
import { useCurrentLocation } from "@/hooks/use-current-location";
import { getCapCopy, type Tier } from "@/lib/location-cap-copy";

export const Route = createFileRoute("/_authenticated/locations/")({
  component: LocationsPage,
});

interface OwnedLoc {
  id: string;
  business_name: string;
  slug: string;
  is_default_location: boolean;
}

function LocationsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { refresh } = useCurrentLocation();
  const [locations, setLocations] = useState<OwnedLoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [cap, setCap] = useState<{ count: number; cap: number; tier: string; canAddMore: boolean; extraSeats: number; cadence: "monthly" | "yearly" | null } | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [{ locations: locs }, capRes] = await Promise.all([
        withAuth(listOwnedLocations)(),
        withAuth(getLocationCap)(),
      ]);
      setLocations(locs as OwnedLoc[]);
      setCap(capRes);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('locations.couldNotLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRename = async (id: string) => {
    if (!editName.trim()) return;
    try {
      await withAuth(renameLocation)({ data: { id, business_name: editName.trim() } });
      toast.success(t('locations.renamed'));
      setEditingId(null);
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('locations.couldNotRename'));
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await withAuth(setDefaultLocation)({ data: { id } });
      toast.success(t('locations.defaultUpdated'));
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('locations.couldNotUpdate'));
    }
  };

  const handleDelete = async (loc: OwnedLoc) => {
    if (!confirm(t('locations.deleteConfirm', { name: loc.business_name }))) return;
    try {
      await withAuth(archiveLocation)({ data: { id: loc.id } });
      toast.success(t('locations.deleted'));
      await Promise.all([load(), refresh()]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('locations.couldNotDelete'));
    }
  };


  const tier: Tier = (cap?.tier as Tier) ?? "none";
  const capCopy = cap && !cap.canAddMore ? getCapCopy(tier, cap.cap, t) : null;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <header className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold">{t('locations.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {cap
                ? t('locations.usageSummary', {
                    count: cap.count,
                    cap: cap.cap === 999 ? t('locations.unlimited') : cap.cap,
                    plural: cap.cap === 1 ? "" : "s",
                    tier: cap.tier === "none" ? t('locations.free') : cap.tier,
                  })
                : ""}
            </p>
          </div>
          <AccessGatedAction>
            <Button
              disabled={cap !== null && !cap.canAddMore}
              onClick={() => {
                if (capCopy) {
                  toast.info(capCopy.blockedToast);
                  return;
                }
                navigate({ to: "/locations/new" });
              }}
              className="gap-2"
              title={
                tier === "growth" && cap && !cap.canAddMore
                  ? t('locations.addSeatFirst')
                  : undefined
              }
            >
              {cap && !cap.canAddMore ? <Lock className="size-4" /> : <Plus className="size-4" />}
              {t('locations.addLocation')}
            </Button>
          </AccessGatedAction>
        </header>

        {capCopy && (
          <Card className="border-tack-amber/40 bg-tack-amber/5">
            <CardContent className="p-4 text-sm">
              {capCopy.bannerHtml.lead}{" "}
              <a
                href={capCopy.bannerHtml.ctaHref}
                className="text-primary underline underline-offset-2"
              >
                {capCopy.bannerHtml.cta}
              </a>
              .
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-display text-lg flex items-center gap-2">
              <Store size={18} /> {t('locations.yourLocations')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {loading ? (
              <p className="text-sm text-muted-foreground">{t('locations.loading')}</p>
            ) : locations.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('locations.noLocations')}</p>
            ) : (
              locations.map((loc) => (
                <div
                  key={loc.id}
                  className="flex items-center gap-3 py-3 px-3 rounded-md border border-border/60"
                >
                  <Store className="size-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    {editingId === loc.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="h-8"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleRename(loc.id);
                            if (e.key === "Escape") setEditingId(null);
                          }}
                        />
                        <Button size="sm" onClick={() => handleRename(loc.id)}>
                          {t('locations.save')}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                          {t('locations.cancel')}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium truncate">{loc.business_name}</p>
                          {loc.is_default_location && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                              {t('locations.default')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">/tack/{loc.slug}</p>
                      </>
                    )}
                  </div>
                  {editingId !== loc.id && (
                    <div className="flex items-center gap-1 shrink-0">
                      {!loc.is_default_location && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(loc.id)}
                          title={t('locations.setDefault')}
                        >
                          <Star size={14} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingId(loc.id);
                          setEditName(loc.business_name);
                        }}
                        title={t('locations.rename')}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(loc)}
                        className="text-destructive hover:text-destructive"
                        title={
                          locations.length <= 1
                            ? t('locations.mustKeepOne', { defaultValue: 'You must keep at least one location' })
                            : t('locations.delete')
                        }
                        disabled={loc.is_default_location || locations.length <= 1}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
